import { useFilePreview } from '@/contexts/file-preview'
import { usePresignedGetUrl } from '@/hooks/usePresignedGetUrl'
import { Attachment } from '@/modules/Upload/types'
import { downloadFile } from '@/utils/function'
import { useCallback } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

// Office file extensions that need encodeURIComponent
export const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']

/**
 * Hook for handling file preview and download with proper URL encoding for Office documents
 */
export const useFilePreviewWithPresigned = () => {
    const { t } = useTranslation()
    const { getPresignedUrl } = usePresignedGetUrl()
    const { preview } = useFilePreview()

    /**
     * Get file extension from attachment
     */
    const getFileExtension = useCallback((attachment: Attachment): string => {
        const fileName =
            attachment?.attachment_file_name ||
            attachment?.attachment_file_url ||
            ''
        return fileName.split('.').pop()?.toLowerCase() || ''
    }, [])

    /**
     * Check if file is an Office document that needs URL encoding
     */
    const isOfficeFile = useCallback(
        (attachment: Attachment): boolean => {
            const extension = getFileExtension(attachment)
            return OFFICE_EXTENSIONS.includes(extension)
        },
        [getFileExtension]
    )

    /**
     * Open file preview with proper URL handling
     */
    const openFilePreview = useCallback(
        async (attachment: Attachment) => {
            const path = attachment?.attachment_file_url
            if (!path) {
                toast.error(t('not-found-file-path'))
                return
            }

            try {
                const presignedUrl = await getPresignedUrl(path)

                if (!presignedUrl) {
                    throw new Error(t('error-getting-file'))
                }

                const fileName =
                    attachment?.attachment_file_name ||
                    path.split('/').pop() ||
                    'file'

                // Check if it's an Office file and encode URL if needed
                const isOffice = isOfficeFile(attachment)
                const finalUrl = isOffice
                    ? encodeURIComponent(presignedUrl)
                    : presignedUrl

                preview(finalUrl, undefined, {
                    attachment_file_name: fileName,
                    attachment_file_url: presignedUrl,
                    attachment_file_type: attachment?.attachment_file_type,
                    attachment_file_size: attachment?.attachment_file_size,
                })
            } catch (error) {
                console.error('Failed to get presigned URL:', error)
                toast.error(t('error-getting-file'))
            }
        },
        [getPresignedUrl, preview, t, isOfficeFile]
    )

    /**
     * Download file with proper URL handling
     */
    const downloadFileWithPresigned = useCallback(
        async (attachment: Attachment) => {
            const path = attachment?.attachment_file_url
            if (!path) {
                toast.error(t('not-found-file-path'))
                return
            }

            try {
                const presignedUrl = await getPresignedUrl(path)

                if (!presignedUrl) {
                    throw new Error(t('error-getting-file'))
                }

                const fileName =
                    attachment?.attachment_file_name ||
                    path.split('/').pop() ||
                    'file'
                downloadFile(presignedUrl, fileName)
            } catch (error) {
                console.error('Failed to download file:', error)
                toast.error(t('error-downloading-file'))
            }
        },
        [getPresignedUrl, t]
    )

    return {
        openFilePreview,
        downloadFileWithPresigned,
        isOfficeFile,
        getFileExtension,
        getPresignedUrl,
        OFFICE_EXTENSIONS,
    }
}
